<script setup>
import { computed, ref } from 'vue';
import ConfigurationCheckSelection from '../ConfigurationCheckSelection';
import ConfigurationPackageInformation from '../ConfigurationPackageInformation';
import ConfigurationPackageInformationOptional from '../ConfigurationPackageInformationOptional';
import { useMainStore } from '../../store/index.js';
import { useConfiguratorApi } from '../../composables/configurator';

import '../../styles/configurator-section.css';

const store = useMainStore();
const { repairMaintenanceBasePrice } = useConfiguratorApi();
const repairMaintenanceSelected = ref(store.selected?.repairMaintenance?.repairMaintenancePackage?.id === 'with');

const emit = defineEmits(['select']);

const props = defineProps({
  items: {
    type: Object,
    default: null,
  },
  serviceSavingsTexts: {
    type: Object,
    default: null,
  },
});

const tachograph = computed(() => {
  if (!props.items) return null;
  return props.items.find(x => x.id === 'repairMaintenanceTachograph');
});

const rmPackage = computed(() => {
  if (!props.items) return null;
  return props.items.find(x => x.id === 'repairMaintenancePackage');
});

const procare = computed(() => {
  if (!props.items) return null;
  return props.items.find(x => x.id === 'repairMaintenanceProcare');
});

const yearlyInspection = computed(() => {
  if (!props.items) return null;
  return props.items.find(x => x.id === 'repairMaintenanceYearlyInspection');
});

const towing = computed(() => {
  if (!props.items) return null;
  return props.items.find(x => x.id === 'repairMaintenanceTowing');
});

const tachographDisabled = computed(() => {
  return !repairMaintenanceSelected.value;
});

const yearlyInspectionDisabled = computed(() => {
  return !repairMaintenanceSelected.value;
});

const towingDisabled = computed(() => {
  return !repairMaintenanceSelected.value;
});

const rmPackageOptional = computed(() => {
  return rmPackage.value?.selectionType === 'checkbox' || store.selectedPaymentType === 'cash';
});

const handleClick = (eventData) => {
  repairMaintenanceSelected.value = eventData.selected;
  let handleDependentOptionsFlag = false;
  if (eventData.selected) {
    store.selected.repairMaintenance.repairMaintenancePackage = rmPackage.value.options.find(o => o.id === 'with');
    if (!eventData.maintainState) handleDependentOptionsFlag = true;
  } else {
    store.selected.repairMaintenance.repairMaintenancePackage = rmPackage.value.options.find(o => o.id === 'without');
    handleDependentOptionsFlag = true;
  }

  if (handleDependentOptionsFlag) {
    if (tachograph.value) store.selected.repairMaintenance.repairMaintenanceTachograph = tachograph.value.options.find(o => o.id === 'without');
    if (yearlyInspection.value) store.selected.repairMaintenance.repairMaintenanceYearlyInspection = yearlyInspection.value.options.find(o => o.id === 'without');
    if (towing.value) store.selected.repairMaintenance.repairMaintenanceTowing = towing.value.options.find(o => o.id === 'without');
  }
};

</script>

<template>
  <section
    v-if="props.items"
    id="repairMaintenance"
    class="configurator-section"
  >
    <div class="configurator-section_title">
      <h2>{{ $i18n('repairMaintenanceHeaderTitle') }}</h2>
    </div>
    <template v-if="rmPackageOptional">
      <ConfigurationPackageInformationOptional
        v-if="rmPackage"
        :item="rmPackage"
        :price-override="repairMaintenanceBasePrice"
        :selected="repairMaintenanceSelected"
        :offer-text="rmPackage.offerText || null"
        :checkbox="true"
        :show-r-m-sliders="store.selectedPaymentType === 'cash'"
        :service-savings-text="props.serviceSavingsTexts?.repairMaintenancePackage"
        @click="handleClick($event)"
      />
    </template>
    <template v-else>
      <ConfigurationPackageInformation
        v-if="rmPackage"
        :item="rmPackage"
        :price-override="repairMaintenanceBasePrice"
        :offer-text="rmPackage.offerText || null"
        :service-savings-text="props.serviceSavingsTexts?.repairMaintenancePackage"
        @loadeddata="handleClick($event)"
      />
    </template>
    <div
      v-if="tachograph || procare"
      class="configurator-section__row"
    >
      <template v-if="tachograph">
        <div>
          <div
            v-if="tachographDisabled"
            class="tachograph-calibration-banner sdds-banner sdds-banner-info el-margin-bottom-8"
          >
            <div class="sdds-banner-body">
              <div class="sdds-banner-subheader">
                {{ $i18n('tachographCalibrationNeedToBeSelected') }}
              </div>
            </div>
          </div>
          <ConfigurationCheckSelection
            v-model="store.selected.repairMaintenance[tachograph.id]"
            :type="tachograph.id"
            :options="tachograph.options"
            :disabled="tachographDisabled"
            @select="emit('select', $event)"
          />
        </div>
      </template>
      <template v-if="procare">
        <div
          v-if="tachograph && procare"
          class="sdds-divider-dark-vertical"
        />
        <ConfigurationCheckSelection
          v-model="store.selected.repairMaintenance[procare.id]"
          :type="procare.id"
          :options="procare.options"
          :offer-text="procare.offerText || null"
          @select="emit('select', $event)"
        />
      </template>
    </div>
    <div
      v-if="yearlyInspection || towing"
      class="configurator-section__row"
    >
      <template v-if="yearlyInspection">
        <div>
          <div
            v-if="yearlyInspectionDisabled"
            class="tachograph-calibration-banner sdds-banner sdds-banner-info el-margin-bottom-8"
          >
            <div class="sdds-banner-body">
              <div class="sdds-banner-subheader">
                {{ $i18n('forYearlyInspectionRepairMaintenanceNeedToBeSelected') }}
              </div>
            </div>
          </div>
          <ConfigurationCheckSelection
            v-model="store.selected.repairMaintenance[yearlyInspection.id]"
            :type="yearlyInspection.id"
            :options="yearlyInspection.options"
            :disabled="yearlyInspectionDisabled"
            :style="{ height: yearlyInspectionDisabled ? 'calc(100% - 56px)' : '100%' }"
            @select="emit('select', $event)"
          />
        </div>
      </template>
      <template v-if="towing">
        <div
          v-if="yearlyInspection && towing"
          class="sdds-divider-dark-vertical"
        />
        <div>
          <div
            v-if="towingDisabled"
            class="tachograph-calibration-banner sdds-banner sdds-banner-info el-margin-bottom-8"
          >
            <div class="sdds-banner-body">
              <div class="sdds-banner-subheader">
                {{ $i18n('forTowingRepairMaintenanceNeedToBeSelected') }}
              </div>
            </div>
          </div>
          <ConfigurationCheckSelection
            v-model="store.selected.repairMaintenance[towing.id]"
            :type="towing.id"
            :options="towing.options"
            :disabled="towingDisabled"
            :style="{ height: yearlyInspectionDisabled ? 'calc(100% - 56px)' : '100%' }"
            @select="emit('select', $event)"
          />
        </div>
      </template>
    </div>
  </section>
</template>

<style scoped>
  .rm-package-select {
    max-width: 70em;
  }
  .configurator-section_text {
    max-width: 70em;
  }
  .configurator-section_title {
    text-align: center;
  }
  .tachograph-calibration-banner {
    max-width: 30em;
  }
  .sdds-divider-dark-vertical {
    height: var(--sdds-spacing-layout-128);
    align-self: center;
  }
  @media only screen and (max-width: 1000px) {
    .sdds-divider-dark-vertical {
      display: none;
    }
    .configurator-section__row {
      flex-direction: column;
    }
  }
</style>
